import type { FirstVenueInput, LegalAcceptanceInput } from '@lib/input/signup.input'
import { logRequestFailure } from '@/helpers/request-outcome'
import { api } from '@/helpers/apiClient'
import { organizationDtoSchema, type OrganizationDto } from '@lib/dto/organization.dto'
import { billingOwnerListDtoSchema, type BillingOwnerListDto } from '@lib/dto/billing-owner.dto'

/** The caller's own organization — the web side of `OrganizationController`. */
class OrganizationService {
  async get(): Promise<OrganizationDto> {
    try {
      return await api.get('/organization', organizationDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load organization', {}, error)
      throw error
    }
  }

  /** The billing owner accepts the terms and privacy policy in force. */
  async acceptLegal(input: LegalAcceptanceInput): Promise<OrganizationDto> {
    try {
      return await api.post('/organization/legal-acceptance', input, organizationDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to record legal acceptance', {}, error)
      throw error
    }
  }

  /** The billing owner creates the first venue while finishing signup. */
  async createFirstVenue(input: FirstVenueInput): Promise<OrganizationDto> {
    try {
      return await api.post('/organization/first-venue', input, organizationDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create the first venue', {}, error)
      throw error
    }
  }

  /** Who billing could be handed to. Only the current billing owner may ask. */
  async billingOwnerCandidates(): Promise<BillingOwnerListDto> {
    try {
      return await api.get('/organization/billing-owner/candidates', billingOwnerListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load billing owner candidates', {}, error)
      throw error
    }
  }

  /** Hand billing to another organization admin. Only the current billing owner may. */
  async transferBillingOwner(userId: string): Promise<OrganizationDto> {
    try {
      return await api.put('/organization/billing-owner', { userId }, organizationDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to transfer billing ownership', { userId }, error)
      throw error
    }
  }
}

export const organizationService = new OrganizationService()
